import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import { LANGUAGES, type Class, type Session } from "@shared/schema";
import { getFlagEmoji } from "./landing";
import { format } from "date-fns";
import { Calendar, Clock, Users, Play, ArrowLeft, Share2, Copy, Check } from "lucide-react";
import { LiveBroadcastIcon } from "@/components/live-broadcast-icon";
import { DEFAULT_THUMBNAIL } from "@/lib/constants";

interface ClassDetailData {
  class: Class;
  sessions: Session[];
  provider: { name: string; avatarUrl?: string | null };
  shareUrl?: string | null;
}

export default function ClassDetail() {
  const { classId } = useParams<{ classId: string }>();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery<ClassDetailData>({
    queryKey: ["/api/classes", classId],
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <Skeleton className="mb-4 h-8 w-48" />
        <Skeleton className="aspect-video w-full rounded-xl" />
        <div className="mt-6 space-y-3">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6">
        <p className="text-muted-foreground">Class not found</p>
      </div>
    );
  }

  const { class: cls, sessions, provider, shareUrl } = data;
  const lang = LANGUAGES.find((l) => l.value === cls.language);
  const liveSessions = sessions.filter((s) => s.status === "LIVE");
  const upcomingSessions = sessions.filter((s) => s.status === "SCHEDULED");
  const pastSessions = sessions.filter((s) => s.status === "ENDED");

  const providerInitials = provider.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="glass-page-bg" />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/classes")}
        className="mb-4"
        data-testid="button-back"
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back to Classes
      </Button>

      <div className="relative overflow-hidden rounded-2xl glass-panel" style={{ padding: 0 }}>
        <img
          src={cls.thumbnailUrl || DEFAULT_THUMBNAIL}
          alt={cls.title}
          className="aspect-video w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 p-6">
          {lang && (
            <Badge variant="secondary" className="mb-2 no-default-hover-elevate glass-card" style={{ color: "rgba(255,255,255,0.8)" }}>
              {getFlagEmoji(lang.flag)} {lang.label}
            </Badge>
          )}
          <h1 className="text-2xl font-bold text-white sm:text-3xl glow-text" data-testid="text-class-title">
            {cls.title}
          </h1>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-primary/20 text-primary text-sm border border-primary/20">
            {providerInitials}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm font-medium" data-testid="text-provider-name">{provider.name}</p>
          <p className="text-xs text-muted-foreground">Instructor</p>
        </div>
      </div>

      {cls.description && (
        <p className="mt-4 text-muted-foreground leading-relaxed" data-testid="text-class-description">
          {cls.description}
        </p>
      )}

      {cls.category && (
        <Badge variant="outline" className="mt-4 glass-input no-default-hover-elevate">
          {cls.category}
        </Badge>
      )}

      {shareUrl && (
        <div className="mt-4 flex items-center gap-2 p-3 glass-share-link" data-testid="share-link-class">
          <Share2 className="h-4 w-4 shrink-0 text-primary" />
          <span className="text-sm font-medium shrink-0">{t("common.shareLink")}:</span>
          <code className="flex-1 truncate text-xs text-muted-foreground" data-testid="text-share-url-class">
            {shareUrl}
          </code>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              } catch {}
            }}
            className="shrink-0 gap-1.5"
            data-testid="button-copy-share-class"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? t("common.copied") : t("common.copyLink")}
          </Button>
        </div>
      )}

      {liveSessions.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-4 flex items-center gap-2 font-semibold text-lg">
            <LiveBroadcastIcon size={16} />
            Live Now
          </h2>
          <div className="space-y-3">
            {liveSessions.map((session) => (
              <SessionItem key={session.id} session={session} isLive user={user} />
            ))}
          </div>
        </div>
      )}

      {upcomingSessions.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-4 flex items-center gap-2 font-semibold text-lg">
            <Calendar className="h-4 w-4 text-primary" />
            Upcoming Sessions
          </h2>
          <div className="space-y-3">
            {upcomingSessions.map((session) => (
              <SessionItem key={session.id} session={session} user={user} />
            ))}
          </div>
        </div>
      )}

      {pastSessions.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-4 flex items-center gap-2 font-semibold text-lg">
            <Clock className="h-4 w-4 text-muted-foreground" />
            Past Sessions
          </h2>
          <div className="space-y-3">
            {pastSessions.map((session) => (
              <SessionItem key={session.id} session={session} isPast user={user} />
            ))}
          </div>
        </div>
      )}

      {sessions.length === 0 && (
        <div className="mt-8 glass-panel-sm flex flex-col items-center justify-center p-8 text-center">
          <Calendar className="mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">No sessions scheduled yet</p>
        </div>
      )}
    </div>
  );
}

function SessionItem({
  session,
  isLive,
  isPast,
  user,
}: {
  session: Session;
  isLive?: boolean;
  isPast?: boolean;
  user: any;
}) {
  const [, navigate] = useLocation();

  return (
    <div
      className={`flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between glass-panel-sm ${isLive ? "border-red-500/20" : ""}`}
      style={isLive ? { boxShadow: "0 0 20px rgba(239, 68, 68, 0.06)" } : undefined}
      data-testid={`card-session-${session.id}`}
    >
      <div className="flex items-center gap-3">
        {isLive && (
          <span className="flex h-3 w-3 shrink-0 relative">
            <span className="absolute inline-flex h-3 w-3 animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
          </span>
        )}
        <div>
          <p className="text-sm font-medium">
            {format(new Date(session.startsAt), "EEEE, MMMM d, yyyy")}
          </p>
          <p className="text-xs text-muted-foreground">
            {format(new Date(session.startsAt), "h:mm a")}
            {session.endsAt && ` - ${format(new Date(session.endsAt), "h:mm a")}`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={isLive ? "default" : isPast ? "secondary" : "outline"} className={isLive ? "glass-badge-live no-default-hover-elevate" : "no-default-hover-elevate"}>
          {isLive ? "Live" : isPast ? "Ended" : "Upcoming"}
        </Badge>
        {isLive && user ? (
          <Button size="sm" onClick={() => navigate(`/live/${session.id}`)} data-testid={`button-join-${session.id}`} style={{ boxShadow: "0 0 12px rgba(59, 130, 246, 0.2)" }}>
            <Play className="mr-1 h-3 w-3" />
            Join
          </Button>
        ) : isLive && !user ? (
          <Button size="sm" onClick={() => navigate("/login")} data-testid={`button-signin-${session.id}`}>
            Sign in to Join
          </Button>
        ) : !isPast && user ? (
          <Button size="sm" variant="outline" onClick={() => navigate(`/sessions/${session.id}`)} data-testid={`button-view-${session.id}`}>
            View Details
          </Button>
        ) : null}
      </div>
    </div>
  );
}
